import React, { useState, useEffect } from "react";
import { Box, Typography, Avatar, Card, LinearProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import axios from "axios";
import { SOCKET } from "../../../../constants/Bidding/BiddingConstants";
import { BACKEND_DOMAIN } from "../../../../common/env";
import "./OBSOverlay.css";

const OBSOverlay = () => {
  const { auctionId } = useParams();
  
  // Auction state
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [currentBid, setCurrentBid] = useState(0);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [auction, setAuction] = useState(null);
  const [auctionName, setAuctionName] = useState("");
  const [teams, setTeams] = useState([]);
  const [isFlipping, setIsFlipping] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [finalizedBid, setFinalizedBid] = useState(null);
  const [countdown, setCountdown] = useState(null);

  // Animation states
  const [playerAnimation, setPlayerAnimation] = useState("");
  const [bidAnimation, setBidAnimation] = useState("");
  const [teamAnimation, setTeamAnimation] = useState("");
  
  // Player stats display and pass animation states
  const [showPlayerStats, setShowPlayerStats] = useState(false);
  const [showPassAnimation, setShowPassAnimation] = useState(false);

  // Helper function to find team by name
  const findTeamByName = (teamName) => {
    if (!teamName || !teams || teams.length === 0) return null;
    const team = teams.find(t => t.teamName === teamName);
    console.log('Finding team by name:', teamName, 'Found:', team);
    return team;
  };
  useEffect(() => {
    // Fetch auction details and teams
    const fetchAuctionDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}`, {
          headers,
        });
        setAuctionName(response.data.title || "Live Auction");
        setAuction(response.data);
      } catch (error) {
        console.error("Error fetching auction details:", error);
        setAuctionName("Live Auction");
      }
    };

    // Fetch teams for logo mapping
    const fetchTeams = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/teams`, {
          headers,
        });
        console.log('Teams loaded:', response.data);
        setTeams(response.data.teams || []);
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };

    fetchAuctionDetails();
    fetchTeams();
  }, [auctionId]);

  useEffect(() => {
    // Join the auction room
    SOCKET.emit('join-auction', { auctionId });

    // Listen for real-time updates
    SOCKET.on("current-state", (state) => {
      if (state.randomPlayerToBid) {
        setCurrentPlayer(state.randomPlayerToBid);
        triggerAnimation('player');
      }
      if (state.selectedTeam) {
        // Find team object from teams array
        const team = findTeamByName(state.selectedTeam);
        setCurrentTeam(team);
        triggerAnimation('team');
      }
      if (state.bidAmount) {
        setCurrentBid(state.bidAmount);
        triggerAnimation('bid');
      }
    });

    SOCKET.on("player-selected", (data) => {
      setCurrentPlayer(data.player);
      setCurrentBid(data.player.baseValue);
      setCurrentTeam(null);
      triggerAnimation('player');
      
      // Show player stats overlay for 5 seconds
      setShowPlayerStats(true);
      setTimeout(() => {
        setShowPlayerStats(false);
      }, 5000);
    });

    SOCKET.on('team-selected', (data) => {
      // Find team object from teams array
      const team = findTeamByName(data.team);
      setCurrentTeam(team);
      triggerAnimation('team');
    });

    SOCKET.on("bid-amount-changed", (data) => {
      setCurrentBid(data.amount);
      triggerAnimation('bid');
    });

    SOCKET.on("bid-finalized", (data) => {
      console.log('OBS Overlay - Received bid-finalized data:', data);
      // Extract finalizedDetails from the data
      const finalizedDetails = data.finalizedDetails || data;
      console.log('OBS Overlay - Processed finalizedDetails:', finalizedDetails);
      
      // Enhance finalizedDetails with current data if logos are missing
      const enhancedFinalizedBid = {
        ...finalizedDetails,
        playerLogo: finalizedDetails.playerLogo || currentPlayer?.playerLogo || '',
        teamLogo: finalizedDetails.teamLogo || (typeof currentTeam === 'object' ? currentTeam.teamLogo : '') || '',
        playerName: finalizedDetails.playerName || currentPlayer?.playerName || 'Player',
        teamName: finalizedDetails.teamName || (typeof currentTeam === 'object' ? currentTeam.teamName : currentTeam) || 'Team',
        bidAmount: finalizedDetails.bidAmount || currentBid
      };
      
      console.log('OBS Overlay - Enhanced finalizedBid:', enhancedFinalizedBid);
      setFinalizedBid(enhancedFinalizedBid);
      setShowCelebration(true);
      setTimeout(() => {
        setShowCelebration(false);
        setFinalizedBid(null);
      }, 5000);
    });

    SOCKET.on("flipping-coin-test", () => {
      setIsFlipping(true);
      setTimeout(() => {
        setIsFlipping(false);
      }, 3500);
    });

    SOCKET.on("reset-bidding", () => {
      setCurrentPlayer(null);
      setCurrentBid(0);
      setCurrentTeam(null);
    });

    // Listen for player pass event
    SOCKET.on("player-pass", () => {
      setShowPassAnimation(true);
      setTimeout(() => {
        setShowPassAnimation(false);
      }, 3000);
    });

    return () => {
      SOCKET.off("current-state");
      SOCKET.off("player-selected");
      SOCKET.off("team-selected");
      SOCKET.off("bid-amount-changed");
      SOCKET.off("bid-finalized");
      SOCKET.off("flipping-coin-test");
      SOCKET.off("reset-bidding");
      SOCKET.off("player-pass");
    };
  }, [auctionId, teams]);

  // Re-process team selection when teams are loaded
  useEffect(() => {
    // If we have a team name but no team object, try to find it again
    if (teams.length > 0 && currentTeam && typeof currentTeam === 'string') {
      const team = findTeamByName(currentTeam);
      if (team) {
        setCurrentTeam(team);
      }
    }
  }, [teams, currentTeam]);

  const triggerAnimation = (type) => {
    switch(type) {
      case 'player':
        setPlayerAnimation('animate-pulse-glow');
        setTimeout(() => setPlayerAnimation(''), 1000);
        break;
      case 'bid':
        setBidAnimation('animate-bid-update');
        setTimeout(() => setBidAnimation(''), 800);
        break;
      case 'team':
        setTeamAnimation('animate-team-select');
        setTimeout(() => setTeamAnimation(''), 1000);
        break;
    }
  };

  return (
    <div className="obs-overlay">
      {/* Coin Flip Animation */}
      {isFlipping && (
        <Box
          className="coin-flip-overlay"
        >
          <Box
            className="coin-flip-wrapper"
            sx={{animation: 'flip 1.2s linear infinite'}}
          >
            {/* Front Side of the Coin */}
            <Box
              component="img"
              src="/images/CPL Logo.png"
              alt="Cricket Bat"
              className="coin-front"
            />
            
            {/* Back Side of the Coin */}
            <Box
              className="coin-back"
              component="img"
              src="/images/Edmonton_Chargers_logo.png"
              alt="Cricket Bat"
            />
          </Box>
          <style>
            {`
              @keyframes flip {
                0% { transform: rotateY(0); }
                100% { transform: rotateY(360deg); }
              }
            `}
          </style>
          <Typography variant="h2" className="flip-text">
            Selecting Next Player...
          </Typography>
        </Box>
      )}

      {/* Player Stats Overlay - Game-style Display */}
      {showPlayerStats && currentPlayer && (
        <div className="player-stats-overlay">
          <div className="player-stats-card">
            <div className="player-stats-header">
              <Avatar 
                src={currentPlayer.playerLogo} 
                className="stats-player-avatar"
              />
              <div className="player-stats-info">
                <Typography variant="h3" className="stats-player-name">
                  {currentPlayer.playerName}
                </Typography>
                <Typography variant="h5" className="stats-player-type">
                  {currentPlayer.playerType}
                </Typography>
                <Typography variant="h4" className="stats-base-value">
                  Base Value: ${currentPlayer.baseValue}
                </Typography>
              </div>
            </div>
            <div className="player-stats-grid">
              <div className="stat-item">
                <Typography className="stat-label">MATCHES</Typography>
                <Typography className="stat-value">{currentPlayer.stats?.matches || 0}</Typography>
              </div>
              <div className="stat-item">
                <Typography className="stat-label">RUNS</Typography>
                <Typography className="stat-value">{currentPlayer.stats?.runs || 0}</Typography>
              </div>
              <div className="stat-item">
                <Typography className="stat-label">WICKETS</Typography>
                <Typography className="stat-value">{currentPlayer.stats?.wickets || 0}</Typography>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Player Pass Animation */}
      {showPassAnimation && currentPlayer && (
        <div className="pass-animation-overlay">
          <div className="pass-animation-content">
            <Avatar 
              src={currentPlayer.playerLogo} 
              className="pass-player-avatar"
            />
            <Typography variant="h2" className="pass-player-name">
              {currentPlayer.playerName}
            </Typography>
            <Typography variant="h1" className="pass-text">
              PASSED
            </Typography>
            <Typography variant="h4" className="pass-subtitle">
              No bids received
            </Typography>
          </div>
        </div>
      )}

      {/* Celebration Overlay */}
      {showCelebration && finalizedBid && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            <Typography variant="h1" className="celebration-title">
              🎉 SOLD! 🎉
            </Typography>
            <div className="celebration-details">
              <Avatar 
                src={finalizedBid.playerLogo || currentPlayer?.playerLogo || ''} 
                className="celebration-player-avatar"
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: '4px solid white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  bgcolor: finalizedBid.playerLogo || currentPlayer?.playerLogo ? 'transparent' : '#3f51b5'
                }}
              >
                {!(finalizedBid.playerLogo || currentPlayer?.playerLogo) && '👤'}
              </Avatar>
              <Typography variant="h2" className="celebration-player-name">
                {finalizedBid.playerName || currentPlayer?.playerName || 'Player'}
              </Typography>
              <Typography variant="h3" className="celebration-to">
                TO
              </Typography>
              <Avatar 
                src={finalizedBid.teamLogo || (typeof currentTeam === 'object' ? currentTeam.teamLogo : '') || ''} 
                className="celebration-team-avatar"
                sx={{ 
                  width: 120, 
                  height: 120,
                  border: '4px solid white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  bgcolor: (finalizedBid.teamLogo || (typeof currentTeam === 'object' ? currentTeam.teamLogo : '')) ? 'transparent' : '#f59e0b'
                }}
              >
                {!(finalizedBid.teamLogo || (typeof currentTeam === 'object' ? currentTeam.teamLogo : '')) && '🏆'}
              </Avatar>
              <Typography variant="h2" className="celebration-team-name">
                {finalizedBid.teamName || (typeof currentTeam === 'object' ? currentTeam.teamName : currentTeam) || 'Team'}
              </Typography>
              <Typography variant="h1" className="celebration-amount">
                ${finalizedBid.bidAmount || currentBid}
              </Typography>
            </div>
          </div>
        </div>
      )}

      {/* Main Overlay Content - Footer Panel Design */}
      <div className="overlay-content">
        {/* Top Bar - Auction Name */}
        <div className="top-bar">
          <Typography variant="h1" className="auction-title">
            {auctionName}
          </Typography>
        </div>

      

        {/* Simplified Footer Panel */}
        <div className="obs-footer-panel">
          {/* Left Section - Leading Team */}
          <div className="obs-footer-left">
            {currentTeam ? (
              <div className="footer-card">
                {/* <Typography variant="h6" className="footer-label">
                  LEADING TEAM
                </Typography> */}
                <div className="footer-value-team">
                    <Avatar 
                      src={typeof currentTeam === 'object' ? currentTeam.teamLogo : ''}
                      className="team-logo-small"
                      sx={{ width: 80, height: 80 }}
                    />
                  <Typography variant="h2" sx={{ 
                    color: '#ffffff', 
                    fontWeight: 'bold', 
                    fontSize: '2.5rem',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    flex: '1 1 auto'
                  }}>
                    {typeof currentTeam === 'object' ? currentTeam.teamName : currentTeam}
                  </Typography>
                </div>
              </div>
            ) : (
              <div className="footer-card">
                <Typography variant="h6" className="footer-label">
                  LEADING TEAM
                </Typography>
                <Typography variant="h5" className="footer-placeholder">
                  No bids yet
                </Typography>
              </div>
            )}
          </div>

          {/* Center Section - Player Photo and Name Only */}
          <div className="obs-footer-center">
            {currentPlayer ? (
              <div className="player-center-card">
                <Avatar 
                  src={currentPlayer.playerLogo} 
                  className="player-avatar-center"
                  sx={{ 
                    width: 240, 
                    height: 240,
                    border: '3px solid rgba(255, 255, 255, 0.8)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                    margin: '0 auto 8px auto'
                  }}
                />
                <Typography variant="h2" className="player-name-center">
                  {currentPlayer.playerName}
                </Typography>
              </div>
            ) : (
              <div className="player-center-card">
                <Typography variant="h3" className="waiting-text-simple">
                  Waiting for player...
                </Typography>
              </div>
            )}
          </div>

          {/* Right Section - Current Bid */}
          <div className="obs-footer-right">
            {currentBid > 0 ? (
              <div className="footer-card">
                <Typography variant="h6" className="footer-label">
                  CURRENT BID
                </Typography>
                <Typography variant="h2" className="footer-value">
                  ${currentBid}
                </Typography>
              </div>
            ) : (
              <div className="footer-card">
                <Typography variant="h6" className="footer-label">
                  CURRENT BID
                </Typography>
                <Typography variant="h4" className="footer-placeholder">
                  No bids yet
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OBSOverlay;